import _ from '@lodash';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getOrders,
  getOrderStatuses,
  selectOrders,
  setCustomer,
  setSalesPerson,
  setSupplier,
  setOrdersSearchText,
  getCustomersList,
  resetOrders,
  getStatuses,
  getSuppliers,
  getShipToLocations,
  getRequestedByList,
  getContainerNumbersList,
} from 'app/main/apps/e-commerce/store/ordersSlice';
import { useDispatch, useSelector } from 'react-redux';
import FuseUtils from '@fuse/utils/FuseUtils';
import { getListCapabilities } from 'app/main/apps/e-commerce/shared/listCapabilities';
import CustomerFilter, { OrderTypeFilter, OrderTyepFilterFunction, IncludeInvoiceFilter, IncludeArchiveFilter, IncludeCancelledFilter, IncludeWaitingApprovalFilter, SalesPersonFilter, ShowOnlyVASFilter, DateTypeFilter , IncludeActionableFilter} from 'app/main/apps/e-commerce/shared/CustomerFilter';
import { parseVasTypesParam } from 'app/main/apps/e-commerce/shared/vasFilterChoices';
import Entities from 'app/main/apps/e-commerce/shared/Entities';
import getColumns from 'app/main/apps/e-commerce/orders/OrdersTableColumns';
import { selectUser } from 'app/store/userSlice';
import { authPermissions } from 'app/auth';
import { upperCase } from 'lodash';
import OrderDialog from '../order/Dialogs/OrderDialog';
import ViewOrderDialog from '../../../dashboards/main/dialogs/ViewOrderDialog';
import ReallocationDialog from '../order/Dialogs/ReallocationDialog';
import { checkNullOrUndefined, hasAnyPermissions, hasPermission, isAdminOrStaff, isCustomer, isDefaultSupplierTypes, isSupplier, isVasTeam } from '../../common/AuraFunctions';
import ChooseSupplier from '../order/Dialogs/ChooseSupplier';
import CreateSupplierOrder from '../order/Dialogs/SupplierShipping';
import getFilterOptions, { supplierViewOptionFromUrl } from './filterOptions';
import ProductDialog from '../product/dialogs/ProductDialog';
import ConfirmInvoiceDialog from '../order/Dialogs/ConfirmInvoiceDialog';
import { getSalesPersons } from '../../client/store/customerSlice';
import viewOrderspermissions from '../../common/PermissionConfig';
import VasPreviewDialog from '../order/Dialogs/VasPreviewDialog';

function Orders(props) {
  const {module} = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isSupplierInvoice = props && props.for && props.for === 'supplier-invoices';
  let viewPermissions = null;
  let permissionModule = null;
  if(isCustomer(user.role) && props.customerModule) { permissionModule = props.customerModule; }
  else if(isSupplier(user.role) && props.supplierModule) { permissionModule = props.supplierModule; }
  else {
    permissionModule = module;
    // Check for permissions in the orders module "PRIV_VIEW_VAS_ORDER", "PRIV_VIEW_VAS_PRODUCTION" to see the list
    if(module === "orders")
      viewPermissions = viewOrderspermissions;
  }
  const isManage = user && FuseUtils.hasPermission(authPermissions.manage, user.role);
  const options = getFilterOptions(props.type, user.role, isSupplierInvoice);
  const [filterType, setSelectedFilterType] = useState(options[0]);
  const [includeInvoice, setIncludeInvoice] = useState(false);
  const [includeArchive, setIncludeArchive] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [includeWaitingApproval, setIncludeWaitingApproval] = useState(false);
  const [reorderDate, setReorderDate] = useState(new Date());
  const [showOnlyVAS, setShowOnlyVAS] = useState(false);
  const [vasTypes, setVasTypes] = useState([]);
  const [actionableOnly, setActionableOnly] = useState(false);
  const ref = useRef();
  const salesPersonRef = useRef();
  const hasFilter = useSelector(state => state.eCommerceApp.orders.params);
  const { shipmentId } = useParams();
  const [cellData, setCellData] = useState({});
  const [searchParams, setSearchParams] = useState({
    orderNo: null,
    invoiceNo: null,
  });
  const blockFetchStatuses =
    filterType &&
      filterType.value &&
      ["container_shipment_view"].includes(filterType.value) && isCustomer(user.role);

  useEffect(() => {
    return () => {
      // following code will run on unmount.
      dispatch(resetOrders());
      setSearchParams({});
    };
  }, []);

  useEffect(() => {
    dispatch(resetOrders());
    setSearchParams({});
    if (isAdminOrStaff(user) && (!['shipment', 'container'].includes(props.type)) && props.for !== 'supplier-invoices') {
      dispatch(getCustomersList({ lt: 'n' }));
    }
    if (isAdminOrStaff(user) && (props.for === 'supplier-invoices' || props.type === 'order')) {
      dispatch(getSuppliers());
    }
    if (isAdminOrStaff(user) && ['order', 'quote', 'rfq'].includes(props.type)) {
      dispatch(getSalesPersons({ isFilter: true }));
    }
    if (isAdminOrStaff(user) && ['order'].includes(props.type)) {
      dispatch(getContainerNumbersList());
    }
    // OrderTyepFilterFunction only returning when shipmentId is New so addded condition here
    // Have to check whether OrderTyepFilterFunction is used anywhere in order page
    if (props.type === 'order' && isManage && ['customer', 'customer_archive'].includes(filterType?.value) && shipmentId === 'new') {
      dispatch(getStatuses('order_type'));
    }
    if ((['order', 'in-production', 'quote', 'rfq', 'fulfilled', 'confirmed', 'container'].includes(props.type)) || isSupplierInvoice) {
      dispatch(getOrderStatuses({ params: { c: 'order_type' } }));
    }
   
    if (isCustomer(user.role) && ['order', 'quote', 'rfq', 'approval'].includes(props.type) && user?.customer?.id) {
      dispatch(getShipToLocations(user?.customer?.id));
      dispatch(getRequestedByList(user?.customer?.id));
    }
    if (isCustomer(user.role) && ['order', 'quote', 'approval'].includes(props.type) && user?.customer?.id) {
      dispatch(getStatuses('customer_internal_status')).then((response) => {
        if (!response.error) {
          const internalStatuses = response?.payload?.data;
          setCellData((prevData) => ({
            ...prevData,
            internal_status: internalStatuses,
          }));
        }
      });
    }
    if (isAdminOrStaff(user) && props.for === 'invoices') {
      // Get payment type list for invoice page filter (Admin/Staff login)
      dispatch(getOrderStatuses({ params: { c: 'payment_type' } }));
    }
    if (!location.search) {
      setSelectedFilterType(options[0])
      setIncludeInvoice(false)
      setIncludeArchive(false)
      setShowOnlyVAS(false)
      setVasTypes([])
      setActionableOnly(false)
    }
  }, [props.for]);

  useEffect(() => {
    if ((['inproduction_orders_view'].includes(filterType?.value)) && isSupplier(user.role)) {
      dispatch(getOrderStatuses({ params: { c: 'inproduction_orders' } }));
    }
    if ((['shipped_shipment_view'].includes(filterType?.value)) && isSupplier(user.role)) {
      dispatch(getOrderStatuses({ params: { c: 'fulfilled_shipments' } }));
    }
    if ((['container_orders_view'].includes(filterType?.value)) && isCustomer(user.role)) {
      dispatch(getOrderStatuses({ params: { c: 'container_order_status' } }));
    }
    if ((['container_shipment_view'].includes(filterType?.value)) && isCustomer(user.role)) {
      dispatch(getOrderStatuses({ params: { c: 'container_status' } }));
    }
    // Gets the rerder status with the reorder as type in the filter
    if ((['reorder'].includes(filterType?.value)) && isManage) {
      dispatch(getOrderStatuses({ params: { c: 'order_status',type: 'reorder' } }));
    }
  }, [filterType?.value]);


  useEffect(() => {
    if (location.search) {
      const sp = new URLSearchParams(location.search);
      if(sp.has("orderNo")) {
        // Same route, different query string (e.g., /quotes?orderNo=123 → /quotes?orderNo=456)
        // React Router does NOT remount the component when only query params change.
        // So we need to manually extract the new orderNo from the URL and update state.
        setSearchParams(prev => ({
          ...prev,
          "orderNo": sp.get("orderNo"), // or whatever value
        }));
      }
      if(sp.has("invoice_no")) {
        // Same route, different query string (e.g., /invoices?invoice_no=123 → /quotes?invoice_no=456)
        // React Router does NOT remount the component when only query params change.
        // So we need to manually extract the new orderNo from the URL and update state.
        setSearchParams(prev => ({
          ...prev,
          "invoiceNo": sp.get("invoice_no"), // or whatever value
        }));
      }
      switch (props.type) {
        case 'order':
          // PGSS-5352: hydrated here because this checkbox renders on the order list (unlike
          // include_cancelled, which belongs under 'invoice').
          if (sp.has('include_waiting_approval')) {
            setIncludeWaitingApproval(sp.get('include_waiting_approval') === 'true')
          }
          if (sp.has('include_invoice_status')) {
            setIncludeInvoice(sp.get('include_invoice_status') === 'true')
          }
          if (sp.has('include_archive')) {
            setIncludeArchive(sp.get('include_archive') === 'true')
          }
          if (sp.has('vas_order')) {
            setShowOnlyVAS(sp.get('vas_order') === 'true')
          }
          if (sp.has('vas_types')) {
            const types = parseVasTypesParam(sp.get('vas_types'))
            setVasTypes(types)
            if (types.length) {
              setShowOnlyVAS(true)
            }
          }
          // This is to handle the back order filter when user navigates from order detail page to back order list
          if (sp.has('back_order')) {
            setSelectedFilterType(options[4])
          }
          // This is to handle the reorder filter when user navigates from order detail page to reorder list
          if (sp.has('reorder')) {
            setSelectedFilterType(options[5])
          }
          if (sp.has('actionableOnly')) {
            setActionableOnly(sp.get('actionableOnly') === 'true')
          }
          break;
        case 'invoice':
          // PGSS-5371: hydrate the Include Cancelled checkbox from the URL. It has to live here, not
          // under 'order', because that is where the control renders — getParams() forwards
          // include_cancelled to the API regardless, so without this a refresh or a shared
          // /invoices?include_cancelled=true link would show cancelled invoices with the box
          // unchecked, and turning them off would take a check-then-uncheck.
          if (sp.has('include_cancelled')) {
            setIncludeCancelled(sp.get('include_cancelled') === 'true')
          }
          if (sp.has('cid') && sp.has('archive'))
            setSelectedFilterType(options[2])
          else if (sp.has('sid') && sp.has('archive'))
            setSelectedFilterType(options[3])
          else if (sp.has('cid'))
            setSelectedFilterType(options[0])
          else if (sp.has('sid'))
            setSelectedFilterType(options[1])
          break;
        case 'rfq':
        case 'quote':
          if (sp.has('actionableOnly')) {
            setActionableOnly(sp.get('actionableOnly') === 'true')
          }
          if (sp.has('archive'))
            setSelectedFilterType(options[1])
          if (sp.has('vas_order')) {
            setShowOnlyVAS(sp.get('vas_order') === 'true')
          }
          break;
        case 'rfd':
        case 'completed':
          if (sp.has('archive'))
            setSelectedFilterType(options[1])
          break;
        case 'in-production':
        case 'fulfilled': {
          // PGSS-5367: follow the view the URL's view_type names. A fixed options[0] / options[1] here put the
          // supplier's Shipped Orders on Shipment View after any filter or page change while the rows were still
          // the Orders View's, so their Send Message disappeared.
          const urlView = supplierViewOptionFromUrl(props.type, options, sp.get('view_type'));
          if (urlView) setSelectedFilterType(urlView);
          break;
        }
        case 'container':
          if (sp.get('view_type') === 'container-orders') {
            setSelectedFilterType(options[1]); 
            break;
          }
          setSelectedFilterType(options[0])
          break;
        default:
          break;
      }
    }
  }, [location]) // Runs when query string changes, even if route path remains the same

  function handleShow(id, isManageP, customer, listType, role) {
    switch (id) {
      case 1:
        // New (type) for request/new?type=request
        return isManageP;
      case 2:
      case 3:
        // Import Multiple (type)
        // Import Single (type)
        return isManageP && ['Orders', 'Quotes'].includes(listType);
      case 4:
        // Import Archive (type)
        return isManageP && customer != null;
      case 5:
        // Customers. Back option
        return isCustomer(role) && !['Quotes'].includes(listType);
      case 6:
        // New (type) /rfqs/request/new?type=request
        return isManageP && ['Orders', 'Quotes', 'Rfqs', 'RFQS'].includes(listType);
      case 7:
        // Export
        return (isSupplier(role) && listType === 'Orders') || (isManageP && ['Orders', 'Quotes', 'Rfqs'].includes(listType));
      case 8:
      case 9:
        // Sample file
        return isManageP && listType === 'Orders';
      default:
        return false;
    }
  }

  function getTableColumns(isManageP, customer, role) {
    let selection = false;
    if (customer) {
      selection = true;
    }
    return getColumns(isManageP, props.type, selection, role, filterType?.value, isSupplierInvoice, user);
  }

  const getType = () => {
    let title = ''
    switch (props.type) {
      case 'approval':
        title = 'Waiting for Approval'
        return title;
      case 'rfq':
        title = `${upperCase(props.type)}S`
        return title;
      case 'completed':
        title = `RFD ${props.type.charAt(0).toUpperCase() + props.type.slice(1)}`
        return title
      case 'rfd':
        title = 'RFPS'
        return title
      case 'confirmed':
        title = 'Confirmed Orders'
        return title
      case 'order':
        if (isDefaultSupplierTypes(user)) {
          title = 'Received Orders'
          return title
        }
        return 'Orders'
      default:
        title = `${_.capitalize(props.type)}s`
        return title;
    }
  }

  const type = isSupplierInvoice ? 'Supplier Invoices' : getType();
  let headerTitle = type
  if (type === 'RFQS' && isCustomer(user.role)) headerTitle = "Quotes Requested"
  if (type === 'Quotes' && isCustomer(user.role)) headerTitle = "Quotes Waiting For Approval"
  if (type === 'Waiting for Approval' && isCustomer(user.role)) headerTitle = "Order Waiting for Approval"
  if (['completed'].includes(props.type)) headerTitle = "RFP Completed"
  if (['fulfilled'].includes(props.type)) headerTitle = "Shipped Orders"
  if (['in-production'].includes(props.type))
    headerTitle = _.startCase(props.type)
  if (['container'].includes(props.type))
    headerTitle = 'Containers'

  const dialog = {
    required: true,
    component: OrderDialog,
    additional: {
      required: true,
      component: ViewOrderDialog,
      data: {
        isDashboard: false,
      },
    },
  };

  const isReorderMode = (['reorder'].includes(filterType?.value) && props.type === 'order' && isManage); // condition to hide other filters in staff or admin when reorder is selected
  
  const filterComponent = filterProps => {
    // PGSS-5381: derive what this screen may show once, instead of asking who the user is at each
    // gate. Same audience as before — listCapabilities mirrors the previous conditions exactly.
    const can = getListCapabilities(user, props.type, { canManage: isManage, isReorderMode, isSupplierInvoice });
    const sendProps = { ...filterProps, filterValue: filterType, setSupplier, setSalesPerson, includeArchive, includeInvoice, includeCancelled, includeWaitingApproval, showOnlyVAS, vasTypes, setSelectedFilterType, setIncludeArchive, setIncludeCancelled, setIncludeWaitingApproval, setIncludeInvoice, setShowOnlyVAS, setVasTypes, pageType: props.type, setReorderDate ,actionableOnly, setActionableOnly }
    return (
      <div className="flex flex-col md:flex-row md:items-start">
        {props.type === 'container' && isManage && <div className='text-center items-center inline-flex font-bold text-16 mr-24 mx-6'>Filter: </div>}
        {(["order", "rfq", "quote", "rfd", "completed", "invoice"].includes(
          props.type
        ) &&
        isManage) ||
          (isCustomer(user.role) && ["order", "invoice"].includes(props.type)) ? (
          <OrderTypeFilter
            {...sendProps}
            options={options}
            role={user.role}
            type={props.type}
            isSupplierInvoice={isSupplierInvoice}
            autoCompleteSxStyle={{
              width: "200px !important",
              ".MuiInput-root": {
                height: "35px",
                minHeight: "35px",
                maxHeight: "35px",
              },
              ".MuiInput-input": {
                padding: "0 !important",
              },
            }}
          />
        ) : null}
        {props.type === "order" &&
          isManage &&
          ["customer", "customer_archive"].includes(filterType?.value) ? (
          <OrderTyepFilterFunction />
        ) : null}
        {!isCustomer(user.role) && !isVasTeam(user.role) && !isSupplier(user.role) && (
          <>
            {!["shipment", "container"].includes(props.type) && !isReorderMode &&(
              <CustomerFilter
                {...sendProps}
                isOrderModule
                pageRef={ref}
                autoCompleteSxStyle={{
                  width: "200px !important",
                  ".MuiInput-root": {
                    height: "35px",
                    minHeight: "35px",
                    maxHeight: "35px",
                  },
                  ".MuiInput-input": {
                    padding: "0 !important",
                  },
                }}
              />
            )}
          </>
        )}
        {isAdminOrStaff(user) &&
          ["order", "quote", "rfq"].includes(props.type) &&
          !['supplier', 'supplier_archive'].includes(filterType?.value) && !isReorderMode &&(
            <>
              <SalesPersonFilter
                {...sendProps}
                isOrderModule
                pageRef={salesPersonRef}
                autoCompleteSxStyle={{
                  width: "200px !important",
                  ".MuiInput-root": {
                    height: "35px",
                    minHeight: "35px",
                    maxHeight: "35px",
                  },
                  ".MuiInput-input": {
                    padding: "0 !important",
                  },
                }}
              />
            </>
          )}

         {isReorderMode && (
            <div className="flex flex-col md:flex-row mx-12 mt-8 md:justify-between w-full">
              <div className="flex flex-row items-center gap-12">
                <DateTypeFilter {...sendProps}
                reorderDate= {reorderDate} />
              </div>
            </div>
          )}  
        {can.filterIncludeInvoice && (
          <>
            <IncludeInvoiceFilter {...sendProps} />
          </>
        )}
        {can.filterIncludeArchive && (
          <>
            <IncludeArchiveFilter {...sendProps} />
          </>
        )}
        {/* PGSS-5371: both the customer and supplier invoice lists. Staff only — a customer's own
            portal already excludes cancelled invoices server-side, so the control would do nothing. */}
        {can.filterCancelledInvoices && (
          <>
            <IncludeCancelledFilter {...sendProps} />
          </>
        )}
        {can.filterWaitingApproval && (
          <>
            <IncludeWaitingApprovalFilter {...sendProps} />
          </>
        )}
        {can.filterShowOnlyVAS && (
          <ShowOnlyVASFilter {...sendProps} />
        )}
        {can.filterActionableOnly && (
          <>
            <IncludeActionableFilter {...sendProps} />
          </>
        )}
        {can.filterSupplierOrderType ? (
          <OrderTypeFilter
            {...sendProps}
            options={options}
            role={user.role}
            type={props.type}
            isSupplierInvoice={isSupplierInvoice}
            autoCompleteSxStyle={{
              width: "200px !important",
              ".MuiInput-root": {
                height: "35px",
                minHeight: "35px",
                maxHeight: "35px",
              },
              ".MuiInput-input": {
                padding: "0 !important",
              },
            }}
          />
        ) : null}

        {(["container"].includes(
          props.type
        ) &&
        isCustomer(user.role)) ? (
          <OrderTypeFilter
            {...sendProps}
            options={options}
            role={user.role}
            type={props.type}
            isSupplierInvoice={isSupplierInvoice}
            autoCompleteSxStyle={{
              width: "200px !important",
              ".MuiInput-root": {
                height: "35px",
                minHeight: "35px",
                maxHeight: "35px",
              },
              ".MuiInput-input": {
                padding: "0 !important",
              },
            }}
          />
        ) : null}
      </div>
    );
  }

  const getMessage = () => {
    const param = { ...hasFilter }
    delete param.stage
    let message = 'Are you sure you want to export all the record(s)'
    if (Object.keys(param).length < 1 || Object.values(param).every(value => value === '')) {
      return message;
    }
    message = `${message} in the current view?`
    return message;
  }

  const getExportFilterType = () => {
    if(props.type === "order") {
      return filterType?.value?.startsWith("supplier") ? "supplier" : "customer";
    }
    return "customer";
  }

  // To get extra buttons array for shipment tracking in staff
  function extraButtonsArray() {
    if(['container'].includes(props.type) && (isManage || isCustomer(user.role))){
      return [
      {
          id: 1,
          name: `Shipment Tracking`,
          show: true,
          icon: "location_on",
          color: "secondary",
          onBtnClick: onShipmentTrackingClick,
        },
    ]
    }
    return [];
  }

  //  navigation to show tracking form
  function onShipmentTrackingClick(){
    navigate("/container-tracking")
  }

  const headerData = {
    headerTitle,
    search: {
      enabled: true,
    },
    filter: {
      component: filterComponent,
    },
    updateShow: true,
    statusCategory: `${props.type === 'approval' ? 'proof' : props.type}_status`,
    permissionModule,
    links: !['invoice', 'approval', 'container'].includes(props.type) ? [
      {
        id: 1,
        title: `New ${props.type}`,
        type: 'new',
        value: '',
        link: type === 'RFQS' ? 'request/new?type=request' : `/${props.type}s/new`,
        show: false,
      },
      {
        id: 2,
        title: `Import Multiple ${type}`,
        display: 'list', // Added field to display import options in list or open file document directly
        type: 'import',
        value: props.type,
        link: 'N/A',
        show: false,
        stage: props.type
      },
      {
        id: 6,
        title: `Import Single ${_.capitalize(props.type)}`,
        display: 'list', // Added field to display import options in list or open file document directly
        type: 'import',
        params: { single: true },
        value: props.type,
        link: 'N/A',
        show: false,
        icon: 'download',
      },
      {
        id: 3,
        title: `Import Archive ${type}`,
        display: 'open_file', // Added field to display import options in list or open file document directly
        type: 'import',
        value: props.type,
        params: { archive: 'yes' },
        link: 'N/A',
        icon: 'archive',
        show: false,
      },
      {
        id: 4,
        title: 'Customers',
        type: 'back',
        value: `${props.type}s`,
        link: '/customers',
        show: false,
      },
      {
        id: 5,
        title: `New ${props.type === 'order' ? 'Order' : 'RFQ'}`,
        type: 'new',
        value: '',
        link: type === 'RFQS' ? '/rfqs/request/new?type=request' : '/products',
        show: false,
      },
      {
        id: 7,
        type: 'export',
        title: `Export ${props.type}s`,
        display: 'list', // Added field to display import options in list or open file document directly
        params: { stage: props.type,  parentFilterType: getExportFilterType() },
        value: props.type,
        stage: props.type,
        link: 'N/A',
        icon:'cloud_upload',
        show: false,
      },
      {
        id: 8,
        type: 'sample_file',
        title: 'Download single order import excel format',
        path: '/sampleFiles/order/single_order_import_excel_format.xlsx'
      },

      {
        id: 9,
        type: 'sample_file',
        title: ' Download single order with supplier price import excel format',
        path: '/sampleFiles/order/single_order_import_with_supplier_price_excel_format.xlsx'
      },
    ] : [],
    extraButtons: extraButtonsArray()
  };
  return (
    <> {(hasPermission(user, "view", permissionModule, isManage) || (viewPermissions && hasAnyPermissions(user, viewPermissions))) && <>
        <Entities
          headerData={headerData}
          entitiesSelector="orders"
          type={props.type}
          entitiesFor={props.for}
          listType={type}
          statusCategory={headerData.statusCategory}
          getEntities={getOrders}
          getEntityStatuses={getOrderStatuses}
          selectEntities={selectOrders}
          setCustomer={setCustomer}
          setSalesPerson={setSalesPerson}
          setEntitiesSearchText={setOrdersSearchText}
          dialog={dialog}
          handleShow={handleShow}
          getColumns={getTableColumns}
          uniqueName={`${props.type}Table`}
          filterType={filterType}
          includeInvoice={includeInvoice}
          includeArchive={includeArchive}
          includeCancelled={includeCancelled}
          setIncludeCancelled={setIncludeCancelled}
          includeWaitingApproval={includeWaitingApproval}
          showOnlyVAS={showOnlyVAS}
          vasTypes={vasTypes}
          reorderDate={reorderDate}
          cellData={cellData}
          searchParams={searchParams}
          actionableOnly={actionableOnly}
          blockFetchStatuses={blockFetchStatuses}
        />
        <ReallocationDialog />
        <ChooseSupplier />
        <CreateSupplierOrder />
        <ProductDialog />
        <ConfirmInvoiceDialog />
        <VasPreviewDialog />
        </>}
    </>
  );
}
export default Orders;
